import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { queryTestRuns } from '@/features/executions/data';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  buildTestDetailPath,
  buildTestRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import { TableV2NestedEmptyState } from '@/features/shared/components/tableSystem/TableV2NestedEmptyState';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  buildTableV2NestedBodyRowSx,
  queryAllTableV2NestedRows,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2DateTimeCell,
  TableV2NestedCollapse,
  TableV2NameCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import { TableV2SkeletonRow } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@/ui/primitives';
import { buildTestRunHeader, type SortableNestedTestRunField } from './SuitesTableV2Headers';
import { buildTestRunColgroup, buildTestRunSkeletonColumns } from './SuitesTableV2Layout';
import type { TagColor } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestRunDto } from '@flowqa/shared';

const defaultNestedTestRunSort = { field: 'timestamp', direction: 'desc' } satisfies ApiSort;

const TestRunNestedRow = ({
  actionsDisabled,
  deleteRowPendingId,
  showActions,
  linksDisabled,
  row,
  tagColorByName,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  showActions: boolean;
  linksDisabled: boolean;
  row: TestRunDto;
  tagColorByName: Partial<Record<string, TagColor>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestRunDto) => void;
  onEditRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => (
  <TableRow sx={buildTableV2NestedBodyRowSx({ depth: 2, hasRenderedDivider: true })}>
    <TableCell>
      <TableV2NameCell
        href={linksDisabled ? null : buildTestDetailPath(row.testSlug ?? row.testId)}
        primary={row.displayName ?? row.testName ?? row.name ?? row.id}
        secondary={formatTableV2RunNumber(row.runNumber)}
        secondaryHref={
          linksDisabled ? null : buildTestRunPath(row.testSlug ?? row.testId, row.runNumber)
        }
      />
    </TableCell>
    <TableCell>
      <TableV2StatusChip status={row.status} />
    </TableCell>
    <TableCell>
      <TableV2DurationCell value={row.durationMs} />
    </TableCell>
    <TableCell>
      <TableV2DateTimeCell value={row.timestamp} />
    </TableCell>
    <TableCell>
      <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
    </TableCell>
    {showActions ? (
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        {actionsDisabled ? null : (
          <TableActionsCell
            rowName={row.displayName ?? row.testName ?? row.name ?? row.id}
            verticalAlign="center"
            deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
            editActionLabel={getEntityActionLabel('edit', 'Test Run')}
            editTagsEntityNoun="test run"
            rowTags={row.tags ?? []}
            onEdit={() => {
              onEditRow(row);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
            onDelete={() => {
              onDeleteRow(row);
            }}
            disableDelete={deleteRowPendingId === row.id}
          />
        )}
      </TableCell>
    ) : null}
  </TableRow>
);

export const SuiteRunExpandedTests = ({
  actionsDisabled,
  childCount,
  deleteRowPendingId,
  expanded,
  linksDisabled,
  organisationId,
  projectId,
  showActions = true,
  sortingDisabled,
  tagColorByName,
  suiteRunId,
  testRunsQueryFn,
  updateRowTagsColorPendingId,
  onDeleteTestRunRow,
  onEditTestRunRow,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  childCount: number;
  deleteRowPendingId: string | null;
  expanded: boolean;
  linksDisabled: boolean;
  organisationId: string | null;
  projectId: string;
  showActions?: boolean;
  sortingDisabled: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  suiteRunId: string;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onEditTestRunRow: (row: TestRunDto) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultNestedTestRunSort);
  const handleSortChange = (field: SortableNestedTestRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const header = buildTestRunHeader({
    includeActions: showActions,
    sort,
    sortingDisabled,
    onSortChange: handleSortChange,
  });
  const colgroup = buildTestRunColgroup(showActions);
  const skeletonColumns = buildTestRunSkeletonColumns(showActions);
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.testRuns,
      'v2-nested',
      organisationId,
      projectId,
      suiteRunId,
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () => {
      const input = {
        page: 1,
        pageSize: 1,
        sorts: [sort],
        filters: buildEqFilter('suiteRunId', suiteRunId),
      };
      return await queryAllTableV2NestedRows(
        input,
        testRunsQueryFn ??
          (async (nextInput) => await queryTestRuns(projectId, organisationId, nextInput)),
      );
    },
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <TableV2NestedFrame depth={2}>
            <Table aria-label="Test runs loading" sx={tableV2NestedDepthSx[2].table}>
              {colgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`suite-test-runs-nested-skeleton-${String(index)}`}
                    columns={skeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : query.data && query.data.items.length > 0 ? (
          <TableV2NestedFrame depth={2}>
            <Table aria-label="Test runs" sx={tableV2NestedDepthSx[2].table}>
              {colgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((testRun) => (
                  <TestRunNestedRow
                    key={testRun.id}
                    actionsDisabled={actionsDisabled}
                    deleteRowPendingId={deleteRowPendingId}
                    showActions={showActions}
                    linksDisabled={linksDisabled}
                    row={testRun}
                    tagColorByName={tagColorByName}
                    updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                    onDeleteRow={onDeleteTestRunRow}
                    onEditRow={onEditTestRunRow}
                    onUpdateRowTagsColor={onUpdateTestRunRowTagsColor}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel="Test runs"
            colgroup={colgroup}
            colSpan={showActions ? 6 : 5}
            depth={2}
            header={header}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};
